/**
 * ref. https://github.com/microcmsio/react-hooks-use-modal/blob/5c7e3f90dd01c1135dec87915e40669e6e8f7251/src/useOverlay.tsx
 */
import { RefObject, useEffect } from 'react'

// https://gomakethings.com/how-to-get-the-first-and-last-focusable-elements-in-the-dom/
// https://html.spec.whatwg.org/multipage/interaction.html#the-tabindex-attribute
const focusablesSelector = [
  'button:not(:disabled)',
  'a[href]',
  'input:not([type="hidden"]):not(:disabled)',
  'select:not(:disabled)',
  'details > summary:first-child',
  'textarea:not(:disabled)',
  '[tabindex]',
  '[contenteditable]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ')

export const useModalKeyHandler = (
  isOpen: boolean,
  close: () => void,
  ref: RefObject<HTMLDivElement | null>
): void => {
  useEffect(() => {
    if (!isOpen) return

    const handleKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        close()
        return
      }

      if (ev.key !== 'Tab') return
      ev.preventDefault()
      if (ref.current === null) return

      const focusables: HTMLElement[] = Array.from(
        ref.current.querySelectorAll(focusablesSelector)
      )

      const currentIndex = focusables.findIndex(
        (element) => element === document.activeElement
      )
      if (currentIndex === -1) {
        focusables[0]?.focus()
        return
      }

      const nextIndex =
        (currentIndex + (ev.shiftKey ? -1 : 1)) % focusables.length
      focusables.at(nextIndex)?.focus()
    }
    if (typeof window === 'undefined') return

    window.document.body.addEventListener('keydown', handleKeydown)
    return () => {
      window.document.body.removeEventListener('keydown', handleKeydown)
    }
  }, [isOpen, close, ref])
}
